import { CampaignDisplayStatus } from "@signalist/shared-types";

/**
 * What the Campaigns-column bulk control may do with a single campaign (PREC-4129).
 *
 * The column asks two questions about the same campaign — "does it count towards the list's
 * Active/Partially active/Paused chip?" and "does Activate all / Pause all act on it?" — and they
 * used to be answered by two separate literals a few lines apart. They disagreed: READY counted
 * towards the chip but was not startable, so a list whose campaign had never run displayed
 * "Paused", offered "Activate all", and did nothing when clicked. Classifying every status once,
 * here, is what keeps the promise and the action in step.
 */
export enum CampaignBulkEligibility {
	/** "Activate all" starts it: paused, or configured and never started. */
	START = "START",
	/** "Pause all" stops it. */
	PAUSE = "PAUSE",
	/** Mid-activation — the cell renders a spinner, not a button, so nothing is promised. */
	IN_FLIGHT = "IN_FLIGHT",
	/** Cannot run at all; excluded from the chip's state, so no button offers to change it. */
	NOT_RUNNABLE = "NOT_RUNNABLE",
}

/**
 * Total over `CampaignDisplayStatus` — deliberately a `switch` with no `default`, so adding a
 * status to the enum fails the build here until somebody decides which bucket it belongs in.
 */
export const classifyCampaignForBulk = (status: CampaignDisplayStatus): CampaignBulkEligibility => {
	switch (status) {
		case CampaignDisplayStatus.PAUSED:
		case CampaignDisplayStatus.VALID:
		case CampaignDisplayStatus.READY:
			return CampaignBulkEligibility.START;
		case CampaignDisplayStatus.ACTIVE:
			return CampaignBulkEligibility.PAUSE;
		case CampaignDisplayStatus.LOADING:
			return CampaignBulkEligibility.IN_FLIGHT;
		case CampaignDisplayStatus.INVALID:
		case CampaignDisplayStatus.ENDED:
			return CampaignBulkEligibility.NOT_RUNNABLE;
	}
};

/** "Activate all" starts this campaign. */
export const canStartInBulk = (status: CampaignDisplayStatus): boolean =>
	classifyCampaignForBulk(status) === CampaignBulkEligibility.START;

/** "Pause all" stops this campaign. */
export const canPauseInBulk = (status: CampaignDisplayStatus): boolean =>
	classifyCampaignForBulk(status) === CampaignBulkEligibility.PAUSE;

/** This campaign counts towards the list's bulk chip (Active / Partially active / Paused). */
export const countsTowardsBulkState = (status: CampaignDisplayStatus): boolean =>
	classifyCampaignForBulk(status) !== CampaignBulkEligibility.NOT_RUNNABLE;
